import type { ConnectionItemProps } from "@wayflyer/flyui";
import { IconCreditCard24Line } from "@wayflyer/flyui-icons/24/line";
import { defineMessages, useIntl } from "react-intl";
import { useSpaNavigate } from "shared-ui";
import type { FundingApplicationSchemas } from "api-types";

import { NAVIGATION_PATHS } from "../../../components/constants";
import { useCreditScoreConsent } from "../../../queries/credit-score-consent/queries";

const MESSAGES = defineMessages({
  softCreditCheckLabel: {
    id: "funding-application: soft credit check label",
    defaultMessage: "Soft credit check",
  },
  creditCheckConsentCompletedSubtitle: {
    id: "funding-application: credit check consent completed subtitle",
    defaultMessage: "{firstName} {lastName}",
  },
  completeCreditCheckConsent: {
    id: "funding-application: complete credit check consent",
    defaultMessage: "Complete",
  },
  creditCheckConsentCompleted: {
    id: "funding-application: credit check consent completed",
    defaultMessage: "Completed",
  },
});

export const useCreditCheckConsentAsConnectionItem = (
  customerDomain: string,
  fundingApplicationStages: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen
): ConnectionItemProps | null => {
  const { navigate } = useSpaNavigate("FundingApp");
  const intl = useIntl();
  const { data: existingConsent } = useCreditScoreConsent(customerDomain);

  if (!fundingApplicationStages.creditConsent || existingConsent?.explicitNoSsn) {
    return null;
  }

  if (existingConsent) {
    return {
      testId: "credit-check-consent",
      label: intl.formatMessage(MESSAGES.softCreditCheckLabel),
      logo: <IconCreditCard24Line />,
      badgeStatus: "positive",
      badgeText: intl.formatMessage(MESSAGES.creditCheckConsentCompleted),
      subtitle: intl.formatMessage(MESSAGES.creditCheckConsentCompletedSubtitle, {
        firstName: existingConsent.firstName,
        lastName: existingConsent.lastName,
      }),
    };
  }

  return {
    testId: "credit-check-consent-incomplete",
    label: intl.formatMessage(MESSAGES.softCreditCheckLabel),
    logo: <IconCreditCard24Line />,
    actions: [
      {
        content: intl.formatMessage(MESSAGES.completeCreditCheckConsent),
        onAction: () => navigate(NAVIGATION_PATHS.CREDIT_CHECK_CONSENT),
      },
    ],
  };
};
